import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { bigwinApi, casinoApi } from '../services/api';
import { BigWin, BigWinSort, BigWinStats, Casino } from '../types';
import { ACCEPT_ATTRIBUTE, ImageUploadError, prepareImageForUpload } from '../utils/imageUpload';
import AuthImage from '../components/AuthImage';
import './BigWins.css';

type TabKey = 'ranking' | 'games' | 'casinos';

const emptyForm = {
  slot_name: '',
  provider: '',
  casino_id: '',
  bet_amount: '',
  win_amount: '',
  win_date: new Date().toISOString().split('T')[0],
  note: ''
};

const BigWins: React.FC = () => {
  const [wins, setWins] = useState<BigWin[]>([]);
  const [stats, setStats] = useState<BigWinStats | null>(null);
  const [casinos, setCasinos] = useState<Casino[]>([]);
  const [slotSuggestions, setSlotSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sort, setSort] = useState<BigWinSort>('multiplier');
  const [activeTab, setActiveTab] = useState<TabKey>('ranking');
  const [search, setSearch] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingWin, setEditingWin] = useState<BigWin | null>(null);
  const [formData, setFormData] = useState(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Bildzustand im Formular: preview zeigt entweder eine neue Data-URL oder das
  // bereits gespeicherte Bild. image bleibt undefined, solange nichts geaendert
  // wurde -- dann laesst der Server das vorhandene Bild unangetastet.
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imagePayload, setImagePayload] = useState<string | null | undefined>(undefined);
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [lightboxWin, setLightboxWin] = useState<BigWin | null>(null);

  const fetchData = useCallback(async (currentSort: BigWinSort) => {
    try {
      const [winsRes, statsRes] = await Promise.all([
        bigwinApi.getAll(currentSort),
        bigwinApi.getStats()
      ]);
      setWins(winsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching big wins:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(sort);
  }, [sort, fetchData]);

  useEffect(() => {
    casinoApi.getAll()
      .then(res => setCasinos(res.data))
      .catch(err => console.error('Error fetching casinos:', err));
    bigwinApi.getSlots()
      .then(res => setSlotSuggestions(res.data))
      .catch(err => console.error('Error fetching slots:', err));
  }, []);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);

  const formatMultiplier = (value: number) =>
    `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: value >= 100 ? 0 : 2 }).format(value)}x`;

  // X-Faktor live im Formular, damit direkt sichtbar ist, was gespeichert wird.
  const liveMultiplier = useMemo(() => {
    const bet = parseFloat(formData.bet_amount);
    const win = parseFloat(formData.win_amount);
    if (!bet || bet <= 0 || isNaN(win)) return null;
    return win / bet;
  }, [formData.bet_amount, formData.win_amount]);

  const filteredWins = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return wins;
    return wins.filter(w =>
      w.slot_name.toLowerCase().includes(term) ||
      (w.provider || '').toLowerCase().includes(term) ||
      (w.casino_name || '').toLowerCase().includes(term)
    );
  }, [wins, search]);

  const openCreateForm = () => {
    setEditingWin(null);
    setFormData(emptyForm);
    setImagePreview(null);
    setImagePayload(undefined);
    setFormError(null);
    setShowForm(true);
  };

  const openEditForm = (win: BigWin) => {
    setEditingWin(win);
    setFormData({
      slot_name: win.slot_name,
      provider: win.provider || '',
      casino_id: win.casino_id ? String(win.casino_id) : '',
      bet_amount: String(win.bet_amount),
      win_amount: String(win.win_amount),
      win_date: win.win_date.split('T')[0],
      note: win.note || ''
    });
    setImagePreview(win.image_url);
    setImagePayload(undefined);
    setFormError(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingWin(null);
    setImagePreview(null);
    setImagePayload(undefined);
    setFormError(null);
  };

  const handleFileSelected = async (file: File | undefined) => {
    if (!file) return;
    setFormError(null);
    setIsProcessingImage(true);
    try {
      // Wird neu kodiert und verkleinert: entfernt Metadaten und angehaengte Daten.
      const dataUrl = await prepareImageForUpload(file);
      setImagePayload(dataUrl);
      setImagePreview(dataUrl);
    } catch (error) {
      setFormError(error instanceof ImageUploadError
        ? error.message
        : 'Bild konnte nicht verarbeitet werden.');
    } finally {
      setIsProcessingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = () => {
    setImagePayload(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const bet = parseFloat(formData.bet_amount);
    const win = parseFloat(formData.win_amount);

    if (!formData.slot_name.trim()) {
      setFormError('Bitte einen Spielnamen angeben.');
      return;
    }
    if (!bet || bet <= 0) {
      setFormError('Der Einsatz muss größer als 0 sein.');
      return;
    }
    if (isNaN(win) || win < 0) {
      setFormError('Bitte einen gültigen Gewinn angeben.');
      return;
    }

    const payload = {
      slot_name: formData.slot_name.trim(),
      provider: formData.provider.trim() || undefined,
      casino_id: formData.casino_id ? parseInt(formData.casino_id, 10) : null,
      bet_amount: bet,
      win_amount: win,
      win_date: formData.win_date,
      note: formData.note.trim() || undefined,
      image: imagePayload
    };

    setIsSaving(true);
    try {
      if (editingWin) {
        await bigwinApi.update(editingWin.id, payload);
      } else {
        await bigwinApi.create(payload);
      }
      await fetchData(sort);
      bigwinApi.getSlots().then(res => setSlotSuggestions(res.data)).catch(() => {});
      closeForm();
    } catch (error: any) {
      const message = error?.response?.data?.message
        || error?.response?.data?.errors?.[0]?.msg
        || 'Speichern fehlgeschlagen.';
      setFormError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Big Win wirklich löschen?')) return;
    try {
      await bigwinApi.delete(id);
      await fetchData(sort);
    } catch (error) {
      console.error('Error deleting big win:', error);
    }
  };

  // Farbabstufung des X-Faktors, damit Ausreisser sofort auffallen.
  const multiplierClass = (value: number) => {
    if (value >= 1000) return 'mega';
    if (value >= 500) return 'huge';
    if (value >= 100) return 'big';
    if (value >= 50) return 'nice';
    return 'normal';
  };

  const rankMedal = (index: number) => {
    if (index === 0) return '🥇';
    if (index === 1) return '🥈';
    if (index === 2) return '🥉';
    return `#${index + 1}`;
  };

  if (isLoading) {
    return <div className="loading">Laden...</div>;
  }

  return (
    <div className="bigwins-page">
      <div className="bigwins-header">
        <h1>Big Wins</h1>
        <button className="btn-new" onClick={openCreateForm}>+ Neuer Big Win</button>
      </div>

      {stats && stats.totals.total_wins > 0 && (
        <div className="bigwins-summary">
          <div className="summary-box">
            <span className="summary-label">Big Wins</span>
            <span className="summary-value">{stats.totals.total_wins}</span>
          </div>
          <div className="summary-box highlight">
            <span className="summary-label">Bester X-Faktor</span>
            <span className="summary-value gold">{formatMultiplier(stats.totals.best_multiplier)}</span>
          </div>
          <div className="summary-box">
            <span className="summary-label">Höchster Gewinn</span>
            <span className="summary-value positive">{formatCurrency(stats.totals.best_win)}</span>
          </div>
          <div className="summary-box">
            <span className="summary-label">Ø X-Faktor</span>
            <span className="summary-value">{formatMultiplier(stats.totals.avg_multiplier)}</span>
          </div>
          <div className="summary-box">
            <span className="summary-label">Gewinne gesamt</span>
            <span className="summary-value positive">{formatCurrency(stats.totals.total_win)}</span>
          </div>
          <div className="summary-box">
            <span className="summary-label">Einsätze gesamt</span>
            <span className="summary-value">{formatCurrency(stats.totals.total_bet)}</span>
          </div>
        </div>
      )}

      <div className="bigwins-tabs">
        <button
          className={activeTab === 'ranking' ? 'active' : ''}
          onClick={() => setActiveTab('ranking')}
        >
          Ranking
        </button>
        <button
          className={activeTab === 'games' ? 'active' : ''}
          onClick={() => setActiveTab('games')}
        >
          Spiele
        </button>
        <button
          className={activeTab === 'casinos' ? 'active' : ''}
          onClick={() => setActiveTab('casinos')}
        >
          Casinos
        </button>
      </div>

      {activeTab === 'ranking' && (
        <>
          <div className="bigwins-controls">
            <input
              type="search"
              className="search-input"
              placeholder="Spiel, Provider oder Casino suchen..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="sort-buttons">
              <span className="sort-label">Sortieren:</span>
              {([
                ['multiplier', 'X-Faktor'],
                ['win', 'Gewinn'],
                ['bet', 'Einsatz'],
                ['date', 'Datum']
              ] as [BigWinSort, string][]).map(([key, label]) => (
                <button
                  key={key}
                  className={sort === key ? 'active' : ''}
                  onClick={() => setSort(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {filteredWins.length === 0 ? (
            <p className="no-data">
              {wins.length === 0
                ? 'Noch keine Big Wins erfasst. Lade deinen ersten Screenshot hoch!'
                : 'Keine Treffer für diese Suche.'}
            </p>
          ) : (
            <div className="bigwins-grid">
              {filteredWins.map((win, index) => (
                <div key={win.id} className={`bigwin-card ${multiplierClass(win.multiplier)}`}>
                  <div className="bigwin-rank">{sort === 'multiplier' ? rankMedal(index) : `#${index + 1}`}</div>

                  <div className="bigwin-image">
                    {win.image_url ? (
                      <AuthImage
                        src={win.image_url}
                        alt={win.slot_name}
                        className="bigwin-screenshot"
                        onClick={() => setLightboxWin(win)}
                      />
                    ) : (
                      <div className="bigwin-no-image">Kein Bild</div>
                    )}
                    <div className={`bigwin-multiplier ${multiplierClass(win.multiplier)}`}>
                      {formatMultiplier(win.multiplier)}
                    </div>
                  </div>

                  <div className="bigwin-body">
                    <div className="bigwin-title">
                      <span className="bigwin-slot">{win.slot_name}</span>
                      {win.provider && <span className="bigwin-provider">{win.provider}</span>}
                    </div>

                    <div className="bigwin-numbers">
                      <div>
                        <span className="bigwin-label">Einsatz</span>
                        <span className="bigwin-value">{formatCurrency(win.bet_amount)}</span>
                      </div>
                      <div>
                        <span className="bigwin-label">Gewinn</span>
                        <span className="bigwin-value positive">{formatCurrency(win.win_amount)}</span>
                      </div>
                    </div>

                    <div className="bigwin-meta">
                      <span>{new Date(win.win_date).toLocaleDateString('de-DE')}</span>
                      {win.casino_name && <span className="bigwin-casino">{win.casino_name}</span>}
                    </div>

                    {win.note && <p className="bigwin-note">{win.note}</p>}

                    <div className="bigwin-actions">
                      <button className="btn-edit-small" onClick={() => openEditForm(win)}>
                        Bearbeiten
                      </button>
                      <button className="btn-delete-small" onClick={() => handleDelete(win.id)}>
                        Löschen
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'games' && (
        <div className="stats-table-wrapper">
          {!stats || stats.games.length === 0 ? (
            <p className="no-data">Noch keine Daten vorhanden.</p>
          ) : (
            <table className="stats-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Spiel</th>
                  <th className="num">Big Wins</th>
                  <th className="num">Bester X</th>
                  <th className="num">Ø X-Faktor</th>
                  <th className="num">Einsätze</th>
                  <th className="num">Gewinne</th>
                </tr>
              </thead>
              <tbody>
                {stats.games.map((game, index) => (
                  <tr key={game.slot_name}>
                    <td className="rank-cell">{index + 1}</td>
                    <td className="name-cell">{game.slot_name}</td>
                    <td className="num">
                      <span className="count-badge">{game.win_count}×</span>
                    </td>
                    <td className={`num multiplier-cell ${multiplierClass(game.best_multiplier)}`}>
                      {formatMultiplier(game.best_multiplier)}
                    </td>
                    <td className="num">{formatMultiplier(game.avg_multiplier)}</td>
                    <td className="num">{formatCurrency(game.total_bet)}</td>
                    <td className="num positive">{formatCurrency(game.total_win)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'casinos' && (
        <div className="stats-table-wrapper">
          {!stats || stats.casinos.length === 0 ? (
            <p className="no-data">Noch keine Big Wins mit Casino-Zuordnung.</p>
          ) : (
            <table className="stats-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Casino</th>
                  <th className="num">Big Wins</th>
                  <th className="num">Bester X</th>
                  <th className="num">Gewinne</th>
                </tr>
              </thead>
              <tbody>
                {stats.casinos.map((casino, index) => (
                  <tr key={casino.casino_id}>
                    <td className="rank-cell">{index + 1}</td>
                    <td className="name-cell">{casino.casino_name}</td>
                    <td className="num">
                      <span className="count-badge">{casino.win_count}×</span>
                    </td>
                    <td className={`num multiplier-cell ${multiplierClass(casino.best_multiplier)}`}>
                      {formatMultiplier(casino.best_multiplier)}
                    </td>
                    <td className="num positive">{formatCurrency(casino.total_win)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal bigwin-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingWin ? 'Big Win bearbeiten' : 'Neuer Big Win'}</h2>

            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Spiel</label>
                  <input
                    type="text"
                    list="slot-suggestions"
                    value={formData.slot_name}
                    onChange={(e) => setFormData({ ...formData, slot_name: e.target.value })}
                    placeholder="z.B. Sugar Rush 1000"
                    maxLength={100}
                    required
                    autoFocus
                  />
                  <datalist id="slot-suggestions">
                    {slotSuggestions.map(name => <option key={name} value={name} />)}
                  </datalist>
                </div>
                <div className="form-group">
                  <label>Provider (optional)</label>
                  <input
                    type="text"
                    value={formData.provider}
                    onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                    placeholder="z.B. Pragmatic Play"
                    maxLength={100}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Einsatz (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formData.bet_amount}
                    onChange={(e) => setFormData({ ...formData, bet_amount: e.target.value })}
                    placeholder="2.00"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Gewinn (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.win_amount}
                    onChange={(e) => setFormData({ ...formData, win_amount: e.target.value })}
                    placeholder="5000.00"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>X-Faktor</label>
                  <div className={`multiplier-display ${liveMultiplier ? multiplierClass(liveMultiplier) : ''}`}>
                    {liveMultiplier !== null ? formatMultiplier(liveMultiplier) : '—'}
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Datum</label>
                  <input
                    type="date"
                    value={formData.win_date}
                    onChange={(e) => setFormData({ ...formData, win_date: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Casino (optional)</label>
                  <select
                    value={formData.casino_id}
                    onChange={(e) => setFormData({ ...formData, casino_id: e.target.value })}
                  >
                    <option value="">— kein Casino —</option>
                    {casinos.map(casino => (
                      <option key={casino.id} value={casino.id}>{casino.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Notiz (optional)</label>
                <input
                  type="text"
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  placeholder="z.B. Super Scatter gekauft"
                  maxLength={1000}
                />
              </div>

              <div className="form-group">
                <label>Screenshot (optional)</label>
                <div className="image-upload">
                  {imagePreview ? (
                    <div className="image-preview">
                      {imagePreview.startsWith('data:') ? (
                        <img src={imagePreview} alt="Vorschau" />
                      ) : (
                        <AuthImage src={imagePreview} alt="Vorschau" />
                      )}
                      <button type="button" className="btn-remove-image" onClick={handleRemoveImage}>
                        Bild entfernen
                      </button>
                    </div>
                  ) : (
                    <div className="image-dropzone" onClick={() => fileInputRef.current?.click()}>
                      {isProcessingImage ? 'Bild wird verarbeitet...' : 'Bild auswählen (PNG, JPEG, GIF, WebP)'}
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPT_ATTRIBUTE}
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileSelected(e.target.files?.[0])}
                  />
                </div>
              </div>

              {formError && <p className="form-error">{formError}</p>}

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={closeForm}>
                  Abbrechen
                </button>
                <button type="submit" className="btn-save" disabled={isSaving || isProcessingImage}>
                  {isSaving ? 'Speichern...' : editingWin ? 'Speichern' : 'Hinzufügen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {lightboxWin && lightboxWin.image_url && (
        <div className="lightbox-overlay" onClick={() => setLightboxWin(null)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <AuthImage
              src={lightboxWin.image_url}
              alt={lightboxWin.slot_name}
              className="lightbox-image"
            />
            <div className="lightbox-info">
              <span className="lightbox-slot">{lightboxWin.slot_name}</span>
              <span className={`lightbox-multiplier ${multiplierClass(lightboxWin.multiplier)}`}>
                {formatMultiplier(lightboxWin.multiplier)}
              </span>
              <span>{formatCurrency(lightboxWin.bet_amount)} → {formatCurrency(lightboxWin.win_amount)}</span>
            </div>
            <button className="lightbox-close" onClick={() => setLightboxWin(null)}>×</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BigWins;
